import { type POSAPricesJavascriptDTO } from "@tootsweet/model/lottery/POSAPrices";
import moment from "moment-timezone";
import { MONTHLY_LOTTERY_MONTH_AND_YEAR_FORMAT } from "../constants";
import CONFIG from "../../model/TSConfig";
import { LANGUAGE, tt } from "../i18n/i18n";
import { type POSATypeJavaDTO } from "@tootsweet/model/lottery/POSAType";
import { EventConfig } from "kadow-jetpack-model";
import { MonthlyLottery } from "kadow-model-monthly-lottery";
import getAmountFormatterForCurrencyAndLanguage from "./getAmountFormatterForCurrencyAndLanguage";

export default class TSUtils {
  public static formatTimestampAsFullDateAndTime(ts: number) {
    return moment(ts, "X").format(tt("date_format_full_with_time"));
  }

  public static timestampToDate(ts?: number) {
    if (!ts) return null;
    return new Date(ts * 1000);
  }

  public static timestampToDateString(ts: number) {
    return moment(ts, "X").format(tt("date_format_full_date"));
  }

  public static timestampToDateStringAbbreviated(ts: number) {
    return moment(ts, "X").format(tt("date_format_abbreviated"));
  }

  public static dateToTimestamp(date: Date | null) {
    if (date == null) {
      return 0;
    }
    return Math.trunc(date.getTime() / 1000);
  }

  public static convertAmountFromCentsToUnits(amt: number | undefined) {
    if (!amt) {
      return 0;
    }
    return Math.trunc(amt * 100) / 10000;
  }

  public static capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public static formatAmountWithLocalizedFormatter(
    amount: number,
    subUnits: number,
    currency: string,
    noDecimals?: boolean
  ) {
    const formattedNumber = noDecimals
      ? NUMBER_FORMATTER_NO_DECIMALS.format(amount / subUnits)
      : NUMBER_FORMATTER.format(amount / subUnits);
    const currencySymbol = TSUtils.getCurrencySymbol(currency);
    return `${formattedNumber} ${currencySymbol}`;
  }

  public static formatAmountUsingIntl(
    amount: number | undefined | null,
    currencySubUnits: number,
    currency: string,
    omitCurrency?: boolean
  ) {
    if (amount === undefined || amount === null) {
      return "#NULL#";
    }
    const finalAmt = amount / currencySubUnits;
    const res = getAmountFormatterForCurrencyAndLanguage(
      currency,
      LANGUAGE
    ).format(finalAmt);
    if (omitCurrency) {
      return res.replace(currency, "");
    }
    return res;
  }

  public static formatAmount(
    amount: number | undefined,
    subUnits: number,
    round: boolean,
    currency: string | null
  ): string {
    if (amount === undefined) {
      return "";
    }

    const curr = currency || "USD";
    const symbol = TSUtils.getCurrencySymbol(curr);
    const separator = TSUtils.getCurrencySeparator(curr);
    const space = TSUtils.getCurrencySpace(curr);

    const integerPart = Math.trunc(amount / subUnits);
    const fractionalPart = round ? 0 : Math.trunc(amount % subUnits);

    if (fractionalPart == 0) {
      return `${integerPart}${space}${symbol}`;
    }

    return `${TSUtils.numberWithCommas(integerPart)}${separator}${
      fractionalPart < 10 ? "0" : ""
    }${fractionalPart}${space}${symbol}`;
  }

  public static formatAmountRoundUp(
    amount: number,
    currency: string,
    subUnits: number
  ): string {
    const finalAmount = Math.ceil(amount / subUnits);
    const symbol = TSUtils.getCurrencySymbol(currency || "USD");
    return `${finalAmount}${symbol}`;
  }

  public static getCurrencySymbol(currency: string): string {
    switch (currency) {
      case "EUR":
        return "€";

      case "GBP":
        return "£";

      case "USD":
        return "$";

      case "CHF":
        return "CHF";

      case "SGD":
        return "S$";

      case "IDR":
        return "Rp";

      case "CAD":
        return "C$";

      case "AUD":
        return "$";

      case "PHP":
        return "₱";

      case "THB":
        return "฿";

      case "MYR":
        return "RM";

      default:
        return "$";
    }
  }

  public static getCurrencySpace(currency: string): string {
    if (
      currency === "CHF" ||
      currency === "SGD" ||
      currency === "IDR" ||
      currency === "CAD" ||
      currency === "MYR"
    ) {
      return " ";
    }
    return "";
  }

  public static getCurrencySeparator(currency: string): string {
    // EUR and CHF use comma, GBP and USD use period
    return currency === "EUR" || currency === "CHF" ? "," : ".";
  }

  public static numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  public static async sleep(ms: number) {
    return await new Promise((resolve, reject) => {
      setTimeout(resolve, ms);
    });
  }

  public static isObjectEmpty(obj: any) {
    if (!obj || typeof obj !== "object") return true; // if no value or the value is not an object, object "is empty"
    const values = Object.values(obj).filter((item) => item !== undefined); // filter the undefined values
    return values.length <= 0;
  }

  public static extractIdFromURL(url: string): string | undefined {
    if (!url) {
      return url;
    }

    const urlWithoutProtocol = url
      .replace("https://", "")
      .replace("http://", "");
    const firstSlash = urlWithoutProtocol.indexOf("/");
    const secondSlash = urlWithoutProtocol.indexOf("/", firstSlash + 1);

    if (secondSlash === -1 || secondSlash === firstSlash) {
      return urlWithoutProtocol.substring(firstSlash + 1);
    }

    if (firstSlash < secondSlash && secondSlash < url.length) {
      return urlWithoutProtocol.substring(firstSlash + 1, secondSlash);
    }

    return url;
  }

  static createJPGameLink = (configId: string, noPreview?: boolean) => {
    return `${CONFIG.jetpackFrontendUrl}/?v=${configId}${
      noPreview ? "" : "&pre=true"
    }`;
  };

  public static characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  public static generateRandomString(length: number) {
    let result = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * this.characters.length);
      result += this.characters.charAt(randomIndex);
    }
    return result;
  }

  public static generateRandomName(file: File) {
    const ext = file.name.split(".").pop();
    return this.generateRandomNameWithExt(ext || "");
  }

  public static generateRandomNameWithExt(ext: string) {
    const randomString = Math.random().toString(36).substring(2, 8);
    return `${randomString}.${ext}`;
  }

  public static isNumber(num: unknown): boolean {
    if (typeof num === "number") {
      return num - num === 0;
    }
    if (typeof num === "string" && num.trim() !== "") {
      return Number.isFinite ? Number.isFinite(+num) : isFinite(+num);
    }
    return false;
  }

  public static isWhitespaceString(str: string) {
    return /^\s*$/.test(str);
  }

  public static replaceWhiteSpaceByNBPSpace(str: string): string {
    return str.replace(/ /g, "\u00A0");
  }

  public static parseSVGFromS3(svgString: string): string {
    if (!svgString) {
      return "";
    }
    svgString = svgString.replace(/<\?xml[^>]*\?>\s*/, "");
    svgString = svgString.replace(/<!--[\s\S]*?-->/g, "");

    const svgMatch = svgString.match(/<svg[\s\S]*<\/svg>/);

    if (svgMatch == null) {
      throw new Error(tt("error_no_svg_element"));
    }

    let svgElement = svgMatch[0];
    svgElement = svgElement.replace(/\s*sodipodi:docname="[^"]*"/, "");

    svgElement = svgElement
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .replace(/>\s+</g, "><")
      .replace(/\s+>/g, ">")
      .replace(/<\s+/g, "<")
      .trim();

    return svgElement;
  }

  public static parseJSON(jsonString: string) {
    const sanitizedString = jsonString
      .replace(/^\uFEFF/, "")
      .replace(/\u00A0/g, " ");

    return JSON.parse(sanitizedString);
  }

  public static getPriceForPOSAQuantity(
    posaType: POSATypeJavaDTO,
    allPosaPrices: POSAPricesJavascriptDTO[],
    desiredQty: number
  ) {
    if (!posaType || !allPosaPrices?.length) {
      return -1;
    }
    const prices = allPosaPrices.find((p) => p._id == posaType.helloPrintSlug);
    if (prices == null || !prices.prices) {
      return -1;
    }
    const quantities = Object.keys(prices.prices).map((i) => parseInt(i));
    quantities.sort((a, b) => a - b);
    let price = -1;
    for (let i = 0; i < quantities.length; i++) {
      const qty = quantities[i];
      if (qty < desiredQty) {
        price = prices.prices[qty];
      } else {
        break;
      }
    }
    return price;
  }

  public static getGLMPeriod = (glm: MonthlyLottery): string => {
    const monthYear = glm._id;

    const m = moment(monthYear, MONTHLY_LOTTERY_MONTH_AND_YEAR_FORMAT);

    return m.format("MMMM YYYY");
  };

  public static formatDate(ts: number) {
    return moment(ts, "X").format(tt("date_format_full_date"));
  }

  public static formatDateRange(minTs: number, maxTs: number): string {
    return tt("date_format_between", {
      startDate: this.formatDate(minTs),
      endDate: this.formatDate(maxTs)
    });
  }

  public static buildMomentObjectForMonthlyLotteryID = (
    month: number,
    year: number
  ) => {
    return moment(`${year}-${month}`, MONTHLY_LOTTERY_MONTH_AND_YEAR_FORMAT);
  };

  public static async fileToBase64(file: File): Promise<string> {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === "string") {
          const base64String = reader.result?.split(",")[1];
          resolve(base64String);
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  public static swapArrayElements(arr: string[], source: number, dest: number) {
    const newArray = [...arr];

    if (source < 0 || source >= arr.length || dest < 0 || dest >= arr.length) {
      throw new Error("Invalid indices");
    }

    [newArray[source], newArray[dest]] = [newArray[dest], newArray[source]];

    return newArray;
  }

  public static isGreaterThanToday = (date: Date): boolean => {
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return compareDate >= today;
  };

  public static checkImageExists(imageUrl: string) {
    if (!imageUrl) {
      return false;
    }
    return new Promise((resolve, reject) => {
      const img = new Image();

      // Handle successful load
      img.onload = () => {
        resolve({
          exists: true,
          width: img.width,
          height: img.height
        });
      };

      // Handle load failure
      img.onerror = () => {
        resolve({
          exists: false,
          width: 0,
          height: 0
        });
      };

      // Set source to trigger loading
      img.src = imageUrl;
    });
  }

  public static isValidUrl(url: string | undefined): boolean {
    if (!url) {
      return false;
    }

    try {
      const urlObj = new URL(url);
      return URL_REGEX.test(urlObj.hostname);
    } catch (_) {
      return false;
    }
  }

  public static shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  public static getRandomItems<T>(arr: T[], count: number): T[] {
    if (arr.length <= count) {
      return [...arr];
    }

    const shuffled = [...arr];
    let currentIndex = shuffled.length;
    let randomIndex;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      [shuffled[currentIndex], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[currentIndex]
      ];
    }

    return shuffled.slice(0, count);
  }

  public static scrollTo(
    container?: HTMLElement | null,
    child?: HTMLElement | null
  ) {
    if (!container || !child) {
      return;
    }
    const containerTop = container.getBoundingClientRect().top;
    const childTop = child.getBoundingClientRect().top;

    const scrollOffset = childTop - containerTop + container.scrollTop - 40;

    container.scrollTo({
      top: scrollOffset,
      behavior: "smooth"
    });
  }

  public static isAxiosError(err: any) {
    return (
      err &&
      typeof err === "object" &&
      "isAxiosError" in err &&
      err.isAxiosError
    );
  }

  public static getNextDate(
    date: number,
    isoMonth: number | string,
    isStartOfDate: boolean,
    minDate?: number
  ) {
    const targetDate = moment()
      .month(isoMonth)
      .date(date)
      [isStartOfDate ? "startOf" : "endOf"]("day")
      [isStartOfDate ? "startOf" : "endOf"]("month");

    const lowerBound =
      minDate !== undefined ? moment.unix(minDate) : moment();

    if (targetDate.isBefore(lowerBound)) {
      targetDate.add(1, "year");
    }

    return targetDate.unix();
  }

  public static getMonthsYearBetween(
    sts: number,
    ets: number,
    excludeCurrentMonth?: boolean
  ) {
    const start = moment(sts, "X").startOf("month");
    const end = moment(ets, "X").startOf("month");

    const months: {
      sts: number;
      ets: number;
      month: number;
      year: number;
    }[] = [];

    const cursor = start.clone();

    while (
      excludeCurrentMonth ? cursor.isBefore(end) : cursor.isSameOrBefore(end)
    ) {
      months.push({
        sts: cursor.unix(),
        ets: cursor.clone().endOf("month").unix(),
        month: cursor.month() + 1,
        year: cursor.year()
      });
      cursor.add(1, "month");
    }

    return months;
  }

  public static moveToFirstPos<T extends { _id: string | number }>(
    array: T[],
    id: string | number
  ): T[] {
    const index = array.findIndex((item) => item._id === id);

    if (index === -1) {
      return [...array];
    }

    return [array[index], ...array.slice(0, index), ...array.slice(index + 1)];
  }

  public static sortNumbers<T>(
    a: T,
    b: T,
    field: keyof T,
    order: "asc" | "desc"
  ): number {
    const aValue = a[field] as number;
    const bValue = b[field] as number;

    return order === "asc" ? aValue - bValue : bValue - aValue;
  }

  public static isHexObjectId(str?: string | null) {
    // 24 = length of an ObjectId in its hex string representation
    return str?.length === 24;
  }

  public static validateEmail(email: string) {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  }

  public static computeNextDatesForEvent(ev: EventConfig, timezone?: string) {
    const tz = timezone || moment.tz.guess();
    const today = moment();
    const startM = moment();
    const endM = moment();

    if (ev.minMonth !== undefined && ev.minMonth !== null) {
      startM.month(ev.minMonth);
    }

    if (ev.minDay !== undefined && ev.minDay !== null) {
      startM.date(ev.minDay);
    }

    if (ev.maxMonth !== undefined && ev.maxMonth !== null) {
      endM.month(ev.maxMonth);
    }

    if (ev.maxDay !== undefined && ev.maxDay !== null) {
      endM.date(ev.maxDay);
    }

    startM.startOf("day");
    endM.endOf("day");

    if (ev.startYear && ev.endYear) {
      startM.year(ev.startYear);
      endM.year(ev.endYear);
    } else if (endM.unix() < today.unix()) {
      startM.add(1, "year");
      endM.add(1, "year");
    }

    startM.startOf("day");
    endM.endOf("day");

    return { startM, endM };
  }
}

const URL_REGEX = /\.[a-z]{2,}$/i;

const NUMBER_FORMATTER_NO_DECIMALS = new Intl.NumberFormat(navigator.language, {
  style: "decimal",
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
});

const NUMBER_FORMATTER = new Intl.NumberFormat(navigator.language, {
  style: "decimal",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2
});
